import { HttpErrorResponse } from "@angular/common/http";
import type {
    TerminologyEntryCreateRequest as GeneratedTerminologyEntryCreateRequest,
    TerminologyEntryDetail as GeneratedTerminologyEntryDetail,
    TerminologyEntrySummary as GeneratedTerminologyEntrySummary,
    TerminologyEntryUpdateRequest as GeneratedTerminologyEntryUpdateRequest,
    TerminologyVocabularyCatalog as GeneratedTerminologyVocabularyCatalog,
    TerminologyVocabularyCatalogItem as GeneratedTerminologyVocabularyCatalogItem,
    TerminologyVocabularyConstraints as GeneratedTerminologyVocabularyConstraints,
    TerminologyVocabularyContentDiffDetail as GeneratedTerminologyVocabularyContentDiffDetail,
    TerminologyVocabularyDefinitionDetail as GeneratedTerminologyVocabularyDefinitionDetail,
    TerminologyVocabularyDefinitionCreateRequest as GeneratedTerminologyVocabularyDefinitionCreateRequest,
    TerminologyVocabularyDraftCreateRequest as GeneratedTerminologyVocabularyDraftCreateRequest,
    TerminologyVocabularyDraftDetail as GeneratedTerminologyVocabularyDraftDetail,
    TerminologyVocabularyDraftSaveRequest as GeneratedTerminologyVocabularyDraftSaveRequest,
    TerminologyVocabularyDraftValidationRequest as GeneratedTerminologyVocabularyDraftValidationRequest,
    TerminologyVocabularyDraftPublishRequest as GeneratedTerminologyVocabularyDraftPublishRequest,
    TerminologyVocabularyPublicationDetail as GeneratedTerminologyVocabularyPublicationDetail,
    TerminologyVocabularyPublicationHistoryItem as GeneratedTerminologyVocabularyPublicationHistoryItem,
    TerminologyVocabularyPublicationRetireRequest as GeneratedTerminologyVocabularyPublicationRetireRequest,
    TerminologyVocabularyRevisionHistoryItem as GeneratedTerminologyVocabularyRevisionHistoryItem,
    TerminologyVocabularyRevisionRestoreRequest as GeneratedTerminologyVocabularyRevisionRestoreRequest,
    TerminologyVocabularyValidationImpactResult as GeneratedTerminologyVocabularyValidationImpactResult,
    TerminologyVocabularyWordingUpdateRequest as GeneratedTerminologyVocabularyWordingUpdateRequest,
    TerminologyVocabularyUsageLocation as GeneratedTerminologyVocabularyUsageLocation,
    TerminologyVocabularyUsageRecord as GeneratedTerminologyVocabularyUsageRecord,
} from "@app/generated/contracts";

export type TerminologyEntrySummary = GeneratedTerminologyEntrySummary;
export type TerminologyEntryDetail = GeneratedTerminologyEntryDetail;
export type TerminologyEntryCreateRequest = GeneratedTerminologyEntryCreateRequest;
export type TerminologyEntryUpdateRequest = GeneratedTerminologyEntryUpdateRequest;
export type TerminologyVocabularyCatalog = GeneratedTerminologyVocabularyCatalog;
export type TerminologyVocabularyCatalogItem = GeneratedTerminologyVocabularyCatalogItem;
export type TerminologyVocabularyConstraints = GeneratedTerminologyVocabularyConstraints;
export type TerminologyVocabularyContentDiffDetail = GeneratedTerminologyVocabularyContentDiffDetail;
export type TerminologyVocabularyDefinitionDetail = GeneratedTerminologyVocabularyDefinitionDetail;
export type TerminologyVocabularyDefinitionCreateRequest = GeneratedTerminologyVocabularyDefinitionCreateRequest;
export type TerminologyVocabularyDraftCreateRequest = GeneratedTerminologyVocabularyDraftCreateRequest;
export type TerminologyVocabularyDraftDetail = GeneratedTerminologyVocabularyDraftDetail;
export type TerminologyVocabularyDraftSaveRequest = GeneratedTerminologyVocabularyDraftSaveRequest;
export type TerminologyVocabularyDraftValidationRequest = GeneratedTerminologyVocabularyDraftValidationRequest;
export type TerminologyVocabularyDraftPublishRequest = GeneratedTerminologyVocabularyDraftPublishRequest;
export type TerminologyVocabularyPublicationDetail = GeneratedTerminologyVocabularyPublicationDetail;
export type TerminologyVocabularyPublicationHistoryItem = GeneratedTerminologyVocabularyPublicationHistoryItem;
export type TerminologyVocabularyPublicationRetireRequest = GeneratedTerminologyVocabularyPublicationRetireRequest;
export type TerminologyVocabularyRevisionHistoryItem = GeneratedTerminologyVocabularyRevisionHistoryItem;
export type TerminologyVocabularyRevisionRestoreRequest = GeneratedTerminologyVocabularyRevisionRestoreRequest;
export type TerminologyVocabularyValidationImpactResult = GeneratedTerminologyVocabularyValidationImpactResult;
export type TerminologyVocabularyWordingUpdateRequest = GeneratedTerminologyVocabularyWordingUpdateRequest;
export type TerminologyVocabularyUsageLocation = GeneratedTerminologyVocabularyUsageLocation;
export type TerminologyVocabularyUsageRecord = GeneratedTerminologyVocabularyUsageRecord;

export interface TerminologyEntryFormValues {
    configurationCode: string;
    versionCode: string;
    tokenCode: string;
    defaultText: string;
    localeCode: string;
    channelCode: string;
    audienceCode: string;
    tenantId: string;
    effectiveStartUtc: string | Date | null;
    effectiveEndUtc: string | Date | null;
}

export type TerminologyUsageSourceSide = "client" | "server" | "authored-document";

export interface TerminologyResolvedReference {
    tokenCode: string;
    resolvedText: string | null;
    isKnown: boolean;
}

export interface TerminologyResolvedText {
    text: string;
    references: TerminologyResolvedReference[];
}

const referencePattern = /\{\{\s*([A-Za-z0-9._:-]+)\s*\}\}/g;

export function parseTerminologyReferenceTokenCodes(text: string): string[] {
    return [...text.matchAll(referencePattern)]
        .map((match) => match[1].trim().toLowerCase())
        .filter((tokenCode, index, values) => values.indexOf(tokenCode) === index);
}

export function resolveTerminologyText(
    text: string,
    options: readonly TerminologyVocabularyCatalogItem[],
): TerminologyResolvedText {
    const values = new Map(options.map((option) => [option.tokenCode.trim().toLowerCase(), option.currentText]));
    const requestedTokens = parseTerminologyReferenceTokenCodes(text);
    const references = requestedTokens.map((tokenCode) => ({
        tokenCode,
        resolvedText: values.get(tokenCode) ?? null,
        isKnown: values.has(tokenCode),
    }));

    const expand = (input: string, active: ReadonlySet<string>, depth: number): string => {
        if (depth >= 8) {
            return input;
        }

        return input.replace(referencePattern, (placeholder, rawToken: string) => {
            const tokenCode = rawToken.trim().toLowerCase();
            const value = values.get(tokenCode);
            if (value === undefined || active.has(tokenCode)) {
                return placeholder;
            }

            return expand(value, new Set([...active, tokenCode]), depth + 1);
        });
    };

    return { text: expand(text, new Set(), 0), references };
}

export interface TerminologyUsageManifestLocation {
    kind: string;
    value: string;
}

export interface TerminologyUsageManifestRecord {
    usageId: string;
    configurationCode: string;
    localeCode?: string | null;
    tokenCode: string;
    definitionKind: string;
    fallbackText: string;
    usageKind: string;
    featureCode: string;
    surfaceCode: string;
    componentCode: string;
    locations: TerminologyUsageManifestLocation[];
    sourceSide: TerminologyUsageSourceSide;
}

export interface TerminologyUsageManifest {
    schemaVersion: string;
    releaseIdentifier: string;
    generatedAtUtc: string;
    sourceSide: "client" | "server" | "merged";
    records: TerminologyUsageManifestRecord[];
}

export function findTerminologyUsages(
    manifest: TerminologyUsageManifest | null,
    configurationCode: string | null | undefined,
    tokenCode: string | null | undefined,
): TerminologyUsageManifestRecord[] {
    const normalizedConfigurationCode = normalizeText(configurationCode).toLowerCase();
    const normalizedTokenCode = normalizeText(tokenCode).toLowerCase();
    if (!manifest || !normalizedConfigurationCode || !normalizedTokenCode) {
        return [];
    }

    return manifest.records.filter((record) =>
        normalizeText(record.configurationCode).toLowerCase() === normalizedConfigurationCode
        && normalizeText(record.tokenCode).toLowerCase() === normalizedTokenCode);
}

export interface TerminologyAdminProblemDetails {
    type?: string;
    title?: string;
    detail?: string;
    status?: number;
    problemCode?: string;
    issueCodes?: string[];
    errors?: Record<string, string[] | string>;
}

export interface TerminologyAdminDisplayError {
    title: string;
    messages: string[];
    statusCode?: number;
    problemCode?: string;
    issueCodes: string[];
    correlationId?: string | null;
}

export function mapTerminologyAdminError(error: unknown): TerminologyAdminDisplayError {
    if (error instanceof HttpErrorResponse) {
        const problem = toProblemDetails(error.error);
        const messages = new Set<string>();

        flattenValidationMessages(problem.errors).forEach((message) => messages.add(message));

        if (typeof problem.detail === "string" && problem.detail.trim().length > 0) {
            messages.add(problem.detail.trim());
        }

        if (messages.size === 0) {
            messages.add(getDefaultErrorMessage(error.status));
        }

        return {
            title: normalizeText(problem.title) || getDefaultErrorTitle(error.status),
            messages: [...messages],
            statusCode: error.status || problem.status,
            problemCode: normalizeText(problem.problemCode) || undefined,
            issueCodes: Array.isArray(problem.issueCodes)
                ? problem.issueCodes.filter((code): code is string => normalizeText(code).length > 0)
                : [],
            correlationId: error.headers?.get("X-Correlation-ID"),
        };
    }

    return {
        title: "Unexpected error",
        messages: ["The request could not be completed. Try again or contact support if the problem persists."],
        issueCodes: [],
    };
}

function toProblemDetails(errorBody: unknown): TerminologyAdminProblemDetails {
    if (!errorBody || typeof errorBody !== "object" || Array.isArray(errorBody)) {
        return {};
    }

    return errorBody as TerminologyAdminProblemDetails;
}

function flattenValidationMessages(errors: TerminologyAdminProblemDetails["errors"]): string[] {
    if (!errors || typeof errors !== "object") {
        return [];
    }

    return Object.values(errors).flatMap((entry) => {
        if (Array.isArray(entry)) {
            return entry
                .map((item) => normalizeText(item))
                .filter((item) => item.length > 0);
        }

        const normalized = normalizeText(entry);
        return normalized.length > 0 ? [normalized] : [];
    });
}

function getDefaultErrorTitle(statusCode: number): string {
    switch (statusCode) {
        case 400:
            return "Request could not be completed";
        case 401:
            return "Sign-in required";
        case 403:
            return "Access denied";
        case 404:
            return "Terminology entry not found";
        case 409:
            return "Conflict detected.";
        case 412:
            return "Wording changed elsewhere";
        default:
            return "Unexpected error";
    }
}

function getDefaultErrorMessage(statusCode: number): string {
    switch (statusCode) {
        case 400:
            return "Review the highlighted values and try again.";
        case 401:
            return "Your session is no longer authenticated. Sign in again and retry the action.";
        case 403:
            return "You do not have permission to perform this action.";
        case 404:
            return "The requested terminology entry could not be found.";
        case 409:
            return "The requested change conflicts with the current terminology state.";
        case 412:
            return "Reload the current terminology entry before applying your changes again.";
        default:
            return "The request could not be completed. Try again or contact support if the problem persists.";
    }
}

function normalizeText(value: unknown): string {
    return typeof value === "string" ? value.trim() : "";
}
